import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import type { ConsentSubmitResult } from '@/hooks/useConsentForm';
import { useOnboardingNavigate } from '@/hooks/useOnboardingNavigate';
import { COMMON_NAMESPACE } from '@/i18n';
import { termsCopy } from '@/Pages/Terms/copy';
import '@/Pages/Terms/TermsPage.sass';

type TermsCtaProps = {
  disabled: boolean;
  isSubmitting: boolean;
  onSubmit: () => Promise<ConsentSubmitResult | null>;
};

export function TermsCta({
  disabled,
  isSubmitting,
  onSubmit,
}: TermsCtaProps): JSX.Element {
  const { t } = useTranslation(COMMON_NAMESPACE);
  const { navigateTo, isNavigating } = useOnboardingNavigate();
  const isBusy = isSubmitting || isNavigating;

  const handleClick = async () => {
    const result = await onSubmit();
    if (result) {
      navigateTo(result.nextStep, { sessionId: result.sessionId });
    }
  };

  return (
    <Box className="terms-cta" data-testid="terms-cta">
      <Button
        type="button"
        variant="contained"
        color="primary"
        fullWidth
        disabled={disabled || isBusy}
        aria-busy={isBusy}
        loading={isBusy}
        className="terms-cta__button"
        loadingIndicator={
          <CircularProgress
            size={22}
            aria-label={t('loading')}
            className="terms-cta__loader"
          />
        }
        loadingPosition="end"
        onClick={() => {
          void handleClick();
        }}
        endIcon={
          isBusy ? undefined : (
            <ArrowForwardRoundedIcon aria-hidden fontSize="small" />
          )
        }
      >
        {termsCopy.cta}
      </Button>
    </Box>
  );
}
